import React, { memo, useMemo } from 'react';
import { useBloc } from '@core/utils/bloc';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { FormSelect, OptionItemProps, FormSelectItem } from '@core/components/form';
import { UserResponse } from '@business/entities/account';
import { Avatar } from '@modules/common/Avatar';
import { CalendarView } from '@modules/common/Calendar';
import { GCCustomCalendar } from '@modules/GCCalendarPage/GCCustomCalendar';
import { GCModulesCalendarBloc } from './GCModulesCalendarBloc';
import { GCCalendarEventObjectType } from '@business/entities/gc_calendar';
import { OrgAddressResponse, OrgId } from '@business/entities/organization';
import { Session } from '@modules/auth/session';
import { formGroup, formControl } from '@core/utils/form';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';

function defineCalendarForm() {
  return formGroup({
    users: formControl<string[]>({
      label: 'Select Users/Teams',
      initialValue: [],
    }),
  });
}

export interface GCModulesCalendarProps {
  objectId?: string;
  objectType?: GCCalendarEventObjectType;
  orgId?: OrgId;
  clientEmails?: string[];
  objectName?: string;
  objectAddress?: OrgAddressResponse | null;
}

export const GCModulesCalendar = memo((props: GCModulesCalendarProps) => {
  const { objectId, objectType, orgId, clientEmails, objectName, objectAddress } = props;
  const bloc = useBloc(GCModulesCalendarBloc, objectType, objectId);
  const authUser = useObservable(Session.user$);
  const form = useMemo(() => defineCalendarForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const users = useObservable(bloc.users$, []);
  const teams = useObservable(bloc.teams$, []);

  const userItems: FormSelectItem<string>[] = useMemo(() => {
    return R.sortBy(users, s => s.firstName).map(s => ({
      label: CommonService.getFullName(s),
      value: R.toString(s.id),
      data: s,
    }));
  }, [users]);

  const teamItems: FormSelectItem<string>[] = useMemo(() => {
    return R.sortBy(teams, s => s.name).map(s => ({
      label: s.name,
      value: `team-${s.id}`,
      data: s,
    }));
  }, [teams]);

  const selectItems = useMemo(() => {
    return [
      { value: 'Users', label: 'Users', options: userItems },
      { value: 'Teams', label: 'Teams', options: teamItems },
    ];
  }, [userItems, teamItems]);

  const emailSelector = useMemo(
    () => (
      <div>
        <FormSelect
          type="multiple"
          withoutGroup
          isClearable={true}
          control={form.controls.users as any}
          items={selectItems}
          itemRenderer={CalendarItemRenderer}
          selectedItemRenderer={CalendarSelectedItemRenderer}
        />
      </div>
    ),
    [form.controls.users, selectItems],
  );

  // Expand team selections to individual user IDs
  const selectedUserItems: string[] = useMemo(() => {
    if (!formValue.users || formValue.users.length === 0) return [];
    const values = formValue.users;
    const selectedUserItems: string[] = [];
    values.forEach(value => {
      if (value.startsWith('team')) {
        const teamId = value.split('-')[1];
        const team = teams.find(t => t.id == teamId);
        if (team) {
          team.users.forEach(u => {
            const findUser = users.find(s => s.id == u.id);
            if (findUser) {
              selectedUserItems.push(R.toString(findUser.id));
            }
          });
        }
      } else {
        selectedUserItems.push(value);
      }
    });
    return selectedUserItems;
  }, [formValue.users, teams, users]);

  const selectedUsers = useMemo(() => {
    if (selectedUserItems.length === 0) {
      // Default: show only auth user for object-specific events
      return [authUser];
    }
    return users.filter(u => selectedUserItems.includes(R.toString(u.id)));
  }, [selectedUserItems, users, authUser]);

  const selectedUserEmails = useMemo(() => {
    return userItems.filter(s => selectedUserItems.includes(s.value)).map(s => s.data!.email);
  }, [selectedUserItems, userItems]);

  // When users/teams are selected, show ALL their events (no object filter)
  // When empty, show only object-specific events
  const effectiveObjectId = selectedUserItems.length === 0 ? objectId : undefined;
  const effectiveObjectType = selectedUserItems.length === 0 ? objectType : undefined;

  return (
    <GCCustomCalendar
      selectedGuestEmails={clientEmails || []}
      selectedUserEmails={selectedUserEmails}
      socials={selectedUsers.map(u => u.id)}
      bloc={bloc}
      emailSelector={emailSelector}
      initialView={CalendarView.resource}
      objectType={effectiveObjectType}
      objectId={effectiveObjectId}
      orgId={orgId}
      selectedUsers={selectedUsers}
      forceLoadView={true}
      objectName={objectName}
      objectAddress={objectAddress}
      originalObjectId={objectId}
      originalObjectType={objectType}
    />
  );
});

export const CalendarItemRenderer = memo((props: OptionItemProps<UserResponse | any>) => {
  const { item } = props;
  const data = item.data!;

  // Handle Team items
  if (item.value.startsWith('team-')) {
    return (
      <div className="d-flex flex-column">
        <div className="d-flex align-items-center">
          <Avatar text={data.name} size={30} />
          <div className="d-flex flex-column ml-2">
            <span className="flex-fill mr-2">{data.name}</span>
            <span className="text-muted small">{data.users?.length || 0} members</span>
          </div>
        </div>
      </div>
    );
  }

  // Handle User items
  return (
    <div className="d-flex flex-column">
      <div className="d-flex  align-items-center">
        {!!data.avatar && <Avatar image={data.avatar} size={30} />}
        <div className="d-flex flex-column ml-2">
          <span className="flex-fill mr-2">{CommonService.getFullName(data)}</span>
          <span className="font-weight-bold">{data.email}</span>
        </div>
      </div>
    </div>
  );
});

export const CalendarSelectedItemRenderer = memo((props: OptionItemProps<UserResponse | any>) => {
  const { item } = props;
  const data = item.data!;

  // Handle Team items
  if (item.value.startsWith('team-')) {
    return (
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column align-items-start">
            <span className="flex-fill mr-2">{data.name}</span>
            <span className="text-muted small">{data.users?.length || 0} members</span>
          </div>
        </div>
      </div>
    );
  }

  // Handle User items
  return (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex flex-column align-items-start">
          <span className="flex-fill mr-2">{CommonService.getFullName(data)}</span>
          <span className="font-weight-bold">{data.email}</span>
        </div>
      </div>
    </div>
  );
});
